import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, CircularProgress, Divider, Avatar, Paper, IconButton } from "@mui/material";
import { downloadBlobFile } from "src/utils/getBlobUrl";
import {
  Cancel,
  FastForwardRounded,
  FastRewindRounded,
  FileDownloadRounded,
  PauseRounded,
  Person,
  PlayArrowRounded,
  SupportAgent,
} from "@mui/icons-material";
import ReactJson from "react-json-view";
import WavesurferPlayer from "@wavesurfer/react";
import Hover from "wavesurfer.js/dist/plugins/hover.esm.js";
import { API_SERVICE_BACKEND } from "src/config";
import axios from "axios";

function formatTime(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
}

function toNormalText(str) {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const AgentBubble = ({ data, isActive }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        mb: 2,
        width: "100%",
      }}
    >
      <Avatar sx={{ mr: 1, background: "transparent", border: "1px solid #e8e8e8" }}>
        <SupportAgent fontSize="small" />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "row",
          alignItems: "end",
          borderRadius: "16px",
          border: isActive ? "1px solid #2196f3" : "1px solid #e8e8e8",
          transition: "all 0.3s ease",
          backgroundColor: isActive ? "rgba(33, 150, 243, 0.05)" : "inherit",
        }}
        className="glass-wrapper-50"
      >
        <Typography variant="body1">{data.message}</Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "left",
            color: "text.secondary",
            minWidth: "fit-content",
            ml: 2,
          }}
        >
          {formatTime(data.time_in_call_secs)}
        </Typography>
      </Paper>
    </Box>
  );
};

const UserBubble = ({ data, isActive }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        mb: 2,
        pr: 1,
        width: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "row",
          alignItems: "end",
          borderRadius: "16px",
          border: isActive ? "1px solid #2196f3" : "1px solid #e8e8e8",
          transition: "all 0.3s ease",
          backgroundColor: isActive ? "rgba(33, 150, 243, 0.05)" : "#f5f6f6",
        }}
        className="glass-wrapper-50"
      >
        <Typography variant="body1">{data.message}</Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "left",
            color: "text.secondary",
            minWidth: "fit-content",
            ml: 2,
          }}
        >
          {formatTime(data.time_in_call_secs)}
        </Typography>
      </Paper>
      <Avatar sx={{ ml: 1, background: "transparent", border: "1px solid #e8e8e8" }}>
        <Person fontSize="small" />
      </Avatar>
    </Box>
  );
};

const ToolCallBubble = ({ toolCallData, toolResultData, isActive }) => {
  const [isResultOpen, setIsResultOpen] = useState(false);
  const handleResultClick = () => {
    setIsResultOpen(!isResultOpen);
  };
  const resultValue = toolResultData && toolResultData.tool_results[0].result_value;
  let JSONResultValue = null;
  if (resultValue) {
    if (typeof resultValue === "string") {
      try {
        JSONResultValue = JSON.parse(resultValue);
      } catch (e) {
        JSONResultValue = null;
      }
    } else {
      JSONResultValue = resultValue;
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        mb: 2,
        width: "100%",
      }}
    >
      <Avatar sx={{ mr: 1, background: "transparent", border: "1px solid #e8e8e8" }}>
        <SupportAgent fontSize="small" />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          borderRadius: "16px",
          border: isActive ? "1px solid #2196f3" : "1px solid #e8e8e8",
          transition: "all 0.3s ease",
          backgroundColor: isActive ? "rgba(33, 150, 243, 0.05)" : "#fbfbfb",
        }}
        className="glass-wrapper-50"
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "row",
            alignItems: "end",
            borderRadius: "16px",
            borderBottom: isResultOpen ? "1px solid #e8e8e8" : "none",
            position: "relative",
            width: "100%",
          }}
          className="glass-wrapper-50"
        >
          <Typography variant="body1" sx={{ mb: 1, width: "100%" }}>
            {toolResultData && !toolResultData.tool_results[0].is_error ? (
              <>
                <span style={{ color: "#00AA38", fontWeight: "bold" }}>Tool succeeded: </span>
                <span>{toNormalText(toolResultData.tool_results[0].tool_name)}</span>
              </>
            ) : toolResultData && toolResultData.tool_results[0].is_error ? (
              <>
                <span style={{ color: "red", fontWeight: "bold" }}>Tool failed: </span>
                <span>{toNormalText(toolResultData.tool_results[0].tool_name)}</span>
              </>
            ) : (
              toolCallData && (
                <>
                  <span style={{ color: "blue", fontWeight: "bold" }}>Tool called: </span>
                  <span>{toNormalText(toolCallData.tool_calls[0].tool_name)}</span>
                </>
              )
            )}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "left",
              color: "text.secondary",
              minWidth: "fit-content",
              ml: 2,
            }}
          >
            {formatTime(toolCallData.time_in_call_secs)}
          </Typography>
          {/* {toolResultData && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                padding: "4px 10px",
                bottom: -14,
                borderRadius: "16px",
                border: "1px solid #e8e8e8",
                cursor: "pointer",
                backgroundColor: "white",
                fontSize: "12px",
              }}
              onClick={handleResultClick}
              className="glass-wrapper-50"
            >
              Result
            </Box>
          )} */}
        </Paper>
        {isResultOpen && (
          <Box
            sx={{
              padding: 2,
              mt: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
            className="glass-wrapper-50"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography>
                <span style={{ fontWeight: "bold" }}>Tool Result:</span>
              </Typography>
              <Cancel fontSize="small" sx={{ cursor: "pointer" }} onClick={handleResultClick} />
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                borderRadius: "16px",
                border: "1px solid #e8e8e8",
                mt: 1,
              }}
              className="glass-wrapper-50"
            >
              {JSONResultValue ? (
                <ReactJson src={JSONResultValue} displayDataTypes={false} displayObjectSizes={false} enableClipboard={false} name={false} />
              ) : (
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {resultValue}
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// Main Conversation Transcript Component
const ConversationTranscript = ({ transcript, conversationId, height }) => {
  const containerRef = useRef(null);
  const messagesRef = useRef(null);
  const messageRefs = useRef([]);

  const [audioUrl, setAudioUrl] = useState("");
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [wavesurfer, setWavesurfer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [formattedCurrentTime, setFormattedCurrentTime] = useState("0:00");
  const [totalTime, setTotalTime] = useState("0:00");
  const [activeMessageIndex, setActiveMessageIndex] = useState(null);

  // Filter valid entries that we want to consider for highlighting
  const validEntries =
    transcript && transcript.length > 0
      ? transcript.filter((entry) => {
          const hasMessage = entry.message !== null;
          const isToolCall = entry.tool_calls && entry.tool_calls.length > 0;
          const isToolResult = entry.tool_results && entry.tool_results.length > 0;
          return (hasMessage || isToolCall) && !isToolResult;
        })
      : [];

  useEffect(() => {
    // Initialize refs array with the correct length
    messageRefs.current = Array(validEntries.length)
      .fill()
      .map(() => React.createRef());
  }, [transcript]);

  useEffect(() => {
    const fetchAudioUrl = async () => {
      console.log("fetching the audio url")
      try {
        if (!conversationId) {
          setIsAudioLoading(false);
          return;
        }
        // const res = await fetch1(`${API_SERVICE_BACKEND}/elevenlab/conversation-audio/${conversationId}`)
        const res = await axios.get(`${API_SERVICE_BACKEND}/elevenlab/conversation-audio/${conversationId}`, {responseType: "blob"})
        if (res.status >= 200 && res.status < 300) {
          const data = res.data;
          const url = URL.createObjectURL(data);
          setAudioUrl(url);
        } else {
          console.error("Error fetching audio:", res.statusText);
        }
      } catch (error) {
        console.error("Error fetching audio URL:", error);
      } finally {
        // Set loading to false even if there's an error to prevent eternal loading 
        if (!audioUrl) {
          setIsAudioLoading(false);
        }
      }
    };

    if (conversationId) {
      fetchAudioUrl();
    }
  }, [conversationId]);

  useEffect(() => {
    // Find which message is currently active based on the timestamp
    let activeIndex = null;

    for (let i = 0; i < validEntries.length; i++) {
      const entry = validEntries[i];
      const nextEntry = i < validEntries.length - 1 ? validEntries[i + 1] : null;

      const entryTime = entry.time_in_call_secs;
      const nextEntryTime = nextEntry ? nextEntry.time_in_call_secs : Infinity;

      if (currentTime >= entryTime && currentTime < nextEntryTime) {
        activeIndex = i;
        break;
      }
    }

    // If we found an active message and it's different from the current one
    if (activeIndex !== null && activeIndex !== activeMessageIndex) {
      setActiveMessageIndex(activeIndex);

      // Scroll the active message into view
      if (messageRefs.current[activeIndex] && messageRefs.current[activeIndex].current) {
        messageRefs.current[activeIndex].current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [currentTime, validEntries, activeMessageIndex]);

  const onPlayPause = () => {
    if (wavesurfer && audioUrl) {
      wavesurfer.playPause();
    }
  };

  const onReady = (ws) => {
    if (ws) {
      setIsAudioLoading(false);
      setWavesurfer(ws);
      setIsPlaying(false);
      setTotalTime(formatTime(ws.getDuration()));
      setFormattedCurrentTime(formatTime(ws.getCurrentTime()));
      setCurrentTime(ws.getCurrentTime());
    }
  };

  const onTimeUpdate = (e) => {
    if (e && e.media) {
      setFormattedCurrentTime(formatTime(e.media.currentTime));
      setCurrentTime(e.media.currentTime);
    }
  };

  const onInteraction = () => {
    if (wavesurfer) {
      wavesurfer.play();
    }
  };

  const handleBackward = () => {
    if (wavesurfer) {
      wavesurfer.skip(-5);
    }
  };

  const handleForward = () => {
    if (wavesurfer) {
      wavesurfer.skip(5);
    }
  };

  const handleDownloadAudio = () => {
    if (audioUrl) {
      downloadBlobFile(audioUrl, "audio.wav");
    }
  };

  return (
    <>
      {!transcript || transcript.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            width: "100%",
          }}
        >
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            No conversation data available.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: height ? height : "475px", // Fixed height container
            overflow: "hidden", // Hide overflow
          }}
          ref={containerRef}
        >
          {/* Sticky Audio Player */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "white",
              pb: 1,
            }}
            className="glass-wrapper-50"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  width: "100%",
                }}
              >
                {!isAudioLoading && audioUrl && (
                  <Box
                    sx={{
                      display: "block",
                      width: "100%",
                      overflow: "hidden",
                      color:theme=>theme.palette.text.primary
                    }}
                  >
                    <WavesurferPlayer
                      height={100}
                      progressColor={"black"}
                      waveColor={"#bbbbbb"}
                      barGap={2}
                      barWidth={2}
                      barHeight={1}
                      cursorWidth={0}
                      url={audioUrl}
                      onReady={onReady}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onInteraction={onInteraction}
                      onTimeupdate={onTimeUpdate}
                    />
                  </Box>
                )}
                {isAudioLoading && (
                  <WavesurferPlayer
                    height={100}
                    progressColor={"black"}
                    waveColor={"#bbbbbb"}
                    barGap={2}
                    barWidth={3}
                    barHeight={3}
                    barRadius={2}
                    cursorWidth={0}
                    url={"/silence_audio.wav"}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  px: 2,
                  width: "100%",
                }}
              >
                <Box>
                  {!isAudioLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Button
                        onClick={onPlayPause}
                        disabled={isAudioLoading || !audioUrl}
                        sx={{
                          minWidth: "44px",
                          minHeight: "36px",
                          width: "44px",
                          height: "36px",
                          padding: 0,
                        }}
                        variant="contained"
                        className="glass-wrapper-50"
                      >
                        {isPlaying ? (
                          <PauseRounded
                            sx={{
                              color: "white",
                              fontSize: "20px",
                            }}
                          />
                        ) : (
                          <PlayArrowRounded
                            sx={{
                              color: "white",
                              fontSize: "20px",
                            }}
                          />
                        )}
                      </Button>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={handleBackward} disabled={isAudioLoading || !audioUrl}>
                          <FastRewindRounded />
                        </IconButton>
                        <IconButton onClick={handleForward} disabled={isAudioLoading || !audioUrl}>
                          <FastForwardRounded />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Button
                      sx={{
                        minWidth: "44px",
                        minHeight: "36px",
                        width: "44px",
                        height: "36px",
                        padding: 0,
                        backgroundColor: "primary.main",
                      }}
                      disabled={true}
                      variant="contained"
                      className="glass-wrapper-50"
                    >
                      <CircularProgress
                        size={20}
                        sx={{
                          color: "white",
                        }}
                      />
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: "bold",
                    }}
                  >
                    {formattedCurrentTime} / {totalTime}
                  </Typography>
                  <Button
                    onClick={handleDownloadAudio}
                    disabled={isAudioLoading || !audioUrl}
                    variant="outlined"
                    sx={{
                      minWidth: "44px",
                      minHeight: "36px",
                      width: "44px",
                      height: "36px",
                      padding: 0,
                      border: "1px solid #e8e8e8",
                    }}
                    className="glass-wrapper-50"
                  >
                    <FileDownloadRounded
                      sx={{
                        color: "text.secondary",
                        fontSize: "20px",
                      }}
                    />
                  </Button>
                </Box>
              </Box>
            </Box>
            <Divider />
          </Box>

          {/* Scrollable Messages Container */}
          <Box
            ref={messagesRef}
            sx={{
              flexGrow: 1,
              overflow: "auto",
              py: 2,
              px: 2,
            }}
          >
            {transcript.map((entry, index) => {
              const isAgent = entry.role === "agent";
              const hasMessage = entry.message !== null;
              const isToolCall = entry.tool_calls && entry.tool_calls.length > 0;
              const isNextStepIsToolResult =
                index < transcript.length - 1 && transcript[index + 1].tool_results && transcript[index + 1].tool_results.length > 0;
              const isToolResult = entry.tool_results && entry.tool_results.length > 0;

              if (!isAgent && !hasMessage && !isToolCall) {
                return null;
              }

              if (isToolResult) {
                return null; // Skip rendering for tool results
              }

              // Calculate the actual index in our filtered valid entries list
              const validIndex = validEntries.findIndex((v) => v.time_in_call_secs === entry.time_in_call_secs && v.message === entry.message);

              const isActive = validIndex === activeMessageIndex;

              return (
                <Box
                  key={index}
                  ref={validIndex !== -1 ? messageRefs.current[validIndex] : null}
                  sx={{
                    display: "flex",
                    justifyContent: isAgent ? "flex-start" : "flex-end",
                    mb: 2,
                    width: "100%",
                  }}
                >
                  <Box sx={{ width: "80%" }}>
                    {hasMessage ? (
                      isAgent ? (
                        <AgentBubble data={entry} isActive={isActive} />
                      ) : (
                        <UserBubble data={entry} isActive={isActive} />
                      )
                    ) : !hasMessage && isToolCall ? (
                      <ToolCallBubble
                        toolCallData={entry}
                        toolResultData={isNextStepIsToolResult ? transcript[index + 1] : null}
                        isActive={isActive}
                      />
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ConversationTranscript;
